import React, { useState } from "react";
import { UploadCloud, Globe, RefreshCw, Sparkles } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import { getErrorMessage } from "../../types";

export interface WorkspaceDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: { id?: number; slug: string; name: string } | null;
  onSuccess: () => Promise<void>;
}

export const WorkspaceDocumentUploadModal: React.FC<WorkspaceDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  onSuccess,
}) => {
  const [docUploadMode, setDocUploadMode] = useState<"file" | "link">("file");
  const [selectedDocFile, setSelectedDocFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [documentType, setDocumentType] = useState("SOP");
  const [fileUrl, setFileUrl] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSelectedDocFile(null);
    setTitle("");
    setDocumentType("SOP");
    setFileUrl("");
    setDescription("");
    setError(null);
    setDocUploadMode("file");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !workspace) return;

    try {
      setUploading(true);
      setError(null);

      if (docUploadMode === "file") {
        if (!selectedDocFile) {
          setError("Please choose a file to upload (.pdf, .docx, .txt, .md).");
          setUploading(false);
          return;
        }

        const { data, error: apiError } = await api.uploadOrgDocumentApiV1B2bSlugDocumentsUploadPost({
          path: { slug },
          body: {
            file: selectedDocFile,
            title: title.trim() || undefined,
            document_type: documentType,
            description: description.trim() || undefined,
            workspace_id: workspace.id,
          },
        });

        if (data) {
          handleClose();
          await onSuccess();
        } else {
          setError(getErrorMessage(apiError, "Failed to upload workspace document"));
        }
      } else {
        if (!fileUrl.trim()) {
          setError("Please provide a valid document link/URL.");
          setUploading(false);
          return;
        }
        if (!title.trim()) {
          setError("Please provide a document title.");
          setUploading(false);
          return;
        }

        const { data, error: apiError } = await api.createWorkspaceDocumentApiV1B2bSlugWorkspacesWsSlugDocumentsPost({
          path: { slug, ws_slug: workspace.slug },
          body: {
            title: title.trim(),
            document_type: documentType,
            file_url: fileUrl.trim(),
            description: description.trim() || undefined,
          },
        });

        if (data) {
          handleClose();
          await onSuccess();
        } else {
          setError(getErrorMessage(apiError, "Failed to save workspace document"));
        }
      }
    } catch (err) {
      console.error("Error saving workspace document:", err);
      setError("An unexpected error occurred while saving the document.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Upload SOP to ${workspace?.name || "Workspace"}`}
      icon={<UploadCloud className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {/* Mode Switch Tabs */}
      <div className="flex p-1 bg-slate-100 rounded-xl mb-4 text-xs font-semibold">
        <button
          type="button"
          onClick={() => setDocUploadMode("file")}
          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition cursor-pointer ${
            docUploadMode === "file"
              ? "bg-white text-indigo-700 shadow-xs font-bold"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <UploadCloud className="w-4 h-4" />
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setDocUploadMode("link")}
          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition cursor-pointer ${
            docUploadMode === "link"
              ? "bg-white text-indigo-700 shadow-xs font-bold"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Globe className="w-4 h-4" />
          Web Link / URL
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {docUploadMode === "file" ? (
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">
              Select Document (.pdf, .docx, .txt, .md) *
            </label>
            <label className="border-2 border-dashed border-slate-200 hover:border-indigo-400 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer bg-slate-50 hover:bg-indigo-50/40 transition">
              <input
                type="file"
                accept=".pdf,.docx,.txt,.md,.markdown"
                required={!selectedDocFile}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setSelectedDocFile(f);
                    if (!title.trim()) {
                      const cleanName = f.name
                        .replace(/\.[^/.]+$/, "")
                        .replace(/[_-]+/g, " ")
                        .trim();
                      setTitle(cleanName);
                    }
                  }
                }}
                className="hidden"
              />
              <UploadCloud className="w-8 h-8 text-indigo-500" />
              {selectedDocFile ? (
                <div className="text-center">
                  <p className="font-bold text-slate-800 text-xs">{selectedDocFile.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {(selectedDocFile.size / 1024).toFixed(1)} KB · Click to choose different file
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="font-semibold text-slate-700 text-xs">Click to browse or drag &amp; drop</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Supports PDF, Word (.docx), Markdown, or Plain Text
                  </p>
                </div>
              )}
            </label>
          </div>
        ) : (
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">Document / Storage Link URL *</label>
            <input
              type="url"
              required
              placeholder="https://storage.googleapis.com/... or cloud document link"
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-mono placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Provide a direct link to the SOP or reference guide for this workspace.
            </p>
          </div>
        )}

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Document Title *</label>
          <input
            type="text"
            required
            placeholder="e.g. Sales Playbook 2026 / QA Deployment SOP"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Document Type</label>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          >
            <option value="SOP">Standard Operating Procedure (SOP)</option>
            <option value="Policy">Company / Team Policy</option>
            <option value="Manual">Training Manual</option>
            <option value="Guide">Onboarding / Engineering Guide</option>
            <option value="Case Study">Case Study / Architecture Doc</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Description (Optional)</label>
          <textarea
            rows={2}
            placeholder="Summary of document purpose and guidelines..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 resize-none"
          />
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploading || (docUploadMode === "file" ? !selectedDocFile : !fileUrl.trim()) || !title.trim()}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-xs transition disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
          >
            {uploading ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                {docUploadMode === "file" ? "Processing & Vectorizing..." : "Saving..."}
              </>
            ) : docUploadMode === "file" ? (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                Save &amp; Vectorize AI
              </>
            ) : (
              "Save Document"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
